import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { clearSignOutPending, markSignOutPending, signOutPending, useAuthStore } from '../store/auth';
import { trackException } from '../telemetry';
import { logBackgroundError, record } from '../observability';
import type {
  SuccessAnswer, ApiWarning, WithWarnings,
  User, UserListRow, RoleRequestRow, Role, Team, Tournament, TournamentReferee, TournamentManager, Ring,
  Registration, RegistrationRow, Bracket, Bout, BoutScoresResponse, Notification,
  AgeGroup, WeightClass, AthleteRecord, AthleteRecordRow, AthleteEntry, AthleteDetailResponse,
  AthleteRecordClaim, AdminAthleteClaim, TeamClaim, BoutPlanView,
  RegisterDto, CreateTournamentDto, UpdateTournamentDto,
  RegisterAthleteDto, UpdateRegistrationDto,
  CreateBoutDto, UpdateBoutDto,
  SubmitRoundScoreDto, SubmitVerdictDto,
  ConfirmBracketDto, ManualBoutAssignment, ReassignBoutAssignment,
  OpenRingPair, WeightClassQuery,
  CreateAthleteRecordDto, UpdateAthleteRecordDto, CoachEditAthleteDto, CoachEditedAthlete,
  AthleteTeamRequest, AthleteTeamInvite, CreateAthleteDto, UpdateAthleteDto,
  UpdateRefereeEntryDto, CreateTeamDto, UpdateTeamDto,
  MarkWeighinDto, PushSubscriptionDto, WeighinDayResponse, WeighinProposal, WeighinStatus,
  AthleteMergeResult, BoutTimerInput, BoutTimerState,
  OfficialRow, OfficialPage, OfficialProfile, OfficialsReport, OfficialsSummaryRow,
} from '../types';
import { API_BASE } from '../utils/appEnv';
import { beginSignOut, endSignOut, markSignOutRejection, signOutRunning, signOutSince, signOutStamp } from '../lib/signOuts';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  // Send/receive cookies on every request (Phase 2). Required for
  // lmf_access (auth), lmf_refresh (refresh), lmf_csrf (CSRF) to ride
  // cross-origin requests in dev (localhost:5173 → localhost:3000) and
  // prod (app.muaythai.lt → api.muaythai.lt). Backend CORS already sets
  // Access-Control-Allow-Credentials: true.
  withCredentials: true,
  // 10s ceiling. Backend p95 is ~50-200ms in normal conditions; 10s gives
  // generous headroom for slow-but-recovering requests while still letting
  // the retry-on-timeout interceptor below kick in promptly when in-arena
  // WiFi stalls a request indefinitely (default axios timeout is 0 = wait
  // forever). 30s was the typical browser-default before this; 10s tightens
  // user-visible latency under flaky network conditions.
  timeout: 10_000,
});

// Read the lmf_csrf cookie (NOT HttpOnly — FE-readable by design) for
// double-submit CSRF on every mutation. Backend middleware compares this
// header to the matching cookie value and 403s on mismatch.
function readCsrfCookie(): string | undefined {
  return document.cookie
    .split('; ')
    .find(row => row.startsWith('lmf_csrf='))
    ?.split('=')[1];
}

/** #666 — Does a sign-out run now, or did one begin or end after this request
 *  began (`lib/signOuts.ts`)? Then a 401 of the request has a known cause: the
 *  sign-out removed the cookies. The interceptor starts no refresh and no
 *  redirect for it.
 *
 *  Before the fix, the code that handled a late answer of the page load
 *  called `setAuth()` and signed the user in again. The bell then asked for
 *  the unread count with no cookie. The 401 sent a user who signed out on
 *  purpose to /login?reason=session_expired. Now `refreshAuth()` in
 *  `store/auth.ts` ignores such an answer.
 *
 *  A request that begins after the sign-out has a new stamp. If the server
 *  ends a session later, the interceptor still sends the user to that page. */
function endedBySignOut(config: RetriableConfig): boolean {
  return signOutSince(config._signOutStamp ?? signOutStamp());
}

/** #666 — The rejection of a request that a sign-out ended. The user signs
 *  out, so no error shows. With the mark, `showError()` and
 *  `logBackgroundError()` show and record nothing. `inlineError()` records
 *  nothing and still returns the text, because a row uses the text to show
 *  that it failed. */
function rejectForSignOut(err: AxiosError): Promise<never> {
  markSignOutRejection(err);
  return Promise.reject(err);
}

api.interceptors.request.use((config) => {
  // #666 — Keep the sign-out stamp of the request. A retry keeps the first stamp.
  (config as RetriableConfig)._signOutStamp ??= signOutStamp();
  // CSRF only on mutations — server-side csrfProtect is GET/HEAD/OPTIONS-exempt.
  const method = (config.method || 'get').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
    const csrf = readCsrfCookie();
    if (csrf) config.headers['X-CSRF-Token'] = csrf;
  }
  return config;
});

/**
 * The transport of the public reads of the ring screens (#664). It has NO
 * session cookie (`withCredentials` stays off), NO interceptor and NO timeout.
 * Two reasons:
 * - With a session cookie, an admin on the TV gets the officials view of the
 *   scores (every card) instead of the approved totals.
 * - A 401 starts the token refresh and the sign-in redirect on a TV that
 *   nobody watches.
 * Pass it as the last argument of a read wrapper: `getBout(id, publicApi)`.
 */
export const publicApi = axios.create({ baseURL: API_BASE });

// ── File upload helper — the axios client, with the multipart type (#457)
/** An upload may take this long; every other call keeps the 10 s timeout. */
export const UPLOAD_TIMEOUT_MS = 120_000;

async function uploadFile(path: string, file: File): Promise<{ url: string }> {
  const fd = new FormData();
  fd.append('photo', file);
  // #457 — through the axios client, as every other call: the CSRF header,
  // the 401 refresh and the `ui_error_shown` record come from its
  // interceptors, and apiError() reads the `code` of a refusal
  // (UPLOAD_NOT_IMAGE, UPLOAD_TOO_LARGE, …). The raw fetch had none of these.
  // The instance default is JSON, which would serialise the FormData: the
  // multipart type makes axios leave the body alone, and the browser adds
  // the boundary.
  // The client timeout is 10 s; a 5 MB photo on venue Wi-Fi needs more. The
  // old fetch had no timeout at all (security review, 2026-09-25).
  const res = await api.post<{ url: string }>(path, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: UPLOAD_TIMEOUT_MS,
  });
  return res.data;
}

// ── 401 handling: try once to refresh the JWT (recovers from roles_version
// mismatch when an admin changes a user's roles). If the refresh itself 401s,
// or the retried request 401s again, we clear storage and redirect to
// /login?reason=session_expired so the Login page can show a translated banner.
//
// Phase 2: the refresh-token endpoint rotates the lmf_access / lmf_refresh
// cookies server-side (no token in response body). We only need to know
// whether the refresh succeeded; the cookie jar carries the new value.
let refreshInflight: Promise<boolean> | null = null;
/** #666 — The abort controller of the refresh in flight, or null. Only
 *  `logout()` aborts it. */
let refreshStop: AbortController | null = null;

/** #666 — The longest wait of a sign-out for a refresh that runs, and the
 *  longest time of the POST of a sign-out. An API request has the same limit
 *  (the `timeout` of `api`). A refresh has no time limit of its own. */
const SIGN_OUT_WAIT_MS = 10_000;
/** #443 — The longest time of the push release of a sign-out. */
const PUSH_RELEASE_WAIT_MS = 3_000;

/** The ONE refresh of this tab: the 401 interceptor and the
 *  `auth:roles_changed` and `auth:anonymous` listeners share it (#455, #501),
 *  so they never send the same `lmf_refresh` cookie twice.
 *
 *  #666 — While a sign-out runs, the tab starts no refresh, and the answer is
 *  false. A refresh that starts then can set new cookies after the sign-out
 *  removed them.
 *
 *  The refresh has no time limit of its own. A refresh that fails sends the
 *  user to /login?reason=session_expired, so a slow refresh on a venue network
 *  must succeed. `refreshStop` keeps the abort controller of the refresh.
 *  Only `logout()` aborts a refresh. It waits 10 s at most for the refresh.
 *  Then it aborts a refresh that still runs, and the answer is false. */
export function tryRefreshToken(): Promise<boolean> {
  if (signOutRunning()) return Promise.resolve(false);
  if (refreshInflight) return refreshInflight;
  const stop = new AbortController();
  refreshStop = stop;
  refreshInflight = fetch(`${API_BASE}/api/auth/email/refresh-token`, {
    method: 'POST',
    credentials: 'include',
    signal: stop.signal,
  })
    .then(res => res.ok)
    .catch(() => false)
    .finally(() => { refreshStop = null; refreshInflight = null; });
  return refreshInflight;
}

/**
 * Full sign-out. Ends the SERVER session, then drops local state.
 *
 * The sign-out buttons used to call clearAuth() alone. That cleared the
 * localStorage `user` blob and the Zustand state, but left `lmf_access` and
 * `lmf_refresh` in the browser and the refresh row live in the database for
 * 30 days — so the session was never actually ended. On a shared venue tablet
 * the next person could mint a whole new session from the surviving refresh
 * cookie and act as the previous user. Verified against a live backend on
 * 2026-09-03 (#213).
 *
 * The sign-out clears the local state also when the POST fails. A user who
 * taps "Sign out" must never stay signed in on the screen.
 * `/api/auth/email/logout` is on the CSRF bypass list, so this needs no
 * X-CSRF-Token.
 *
 * The push release runs first, while the session can still authorise it,
 * and for 3 s at most: a stalled venue network must not hold the sign-out.
 *
 * #666 — `logout()` begins and ends each sign-out in `lib/signOuts.ts`. A 401
 * of a request that began before or during a sign-out is not an expired
 * session (`endedBySignOut()`). A refresh that runs can rotate the session.
 * The sign-out waits for it, so the POST sends the newest refresh cookie and
 * ends the newest session row.
 *
 * The sign-out waits 10 s at most. Then it aborts a refresh that still runs,
 * before the push release and the POST, so no refresh can set cookies after
 * the POST. The sign-out aborts its POST after 10 s, so a stalled network
 * cannot keep the sign-out open.
 *
 * One sign-out runs at a time. A second call gets the promise of the sign-out
 * that runs, so a tap on each of the two buttons sends one POST. The
 * `useBusy()` lock of each button shows the busy label of that button.
 *
 * Only the answer to the POST removes the HttpOnly cookies. The result is
 * `{ complete: false }` when the POST fails, gets no answer in 10 s, or gets
 * an answer that is not 2xx. Then the server can still hold the session, and
 * the browser still holds the cookies. The key of `signOutPending()`
 * (`store/auth.ts`) stays set, and the next page load sends the POST again
 * (`finishPendingSignOut()`). A blocked or full storage refuses the key: then
 * `retryAtNextLoad` is false, and nothing sends the POST again. The two
 * buttons show the warning of the case until the user closes it.
 */
export function logout(): Promise<SignOutResult> {
  signOutInflight ??= runSignOut().finally(() => { signOutInflight = null; });
  return signOutInflight;
}

/** #666 — The result of a sign-out. */
export interface SignOutResult {
  /** True when the server answered the POST with 2xx: the server ended the
   *  session, and the answer removed the cookies. */
  complete: boolean;
  /** True when the sign-out is not complete and the key of
   *  `signOutPending()` is stored: the next page load sends the POST again.
   *  False when the browser refused the key (a blocked or full storage). The
   *  warning then tells the user to sign in and sign out again. */
  retryAtNextLoad: boolean;
}

/** #666 — The sign-out that runs now, or null. */
let signOutInflight: Promise<SignOutResult> | null = null;

/** #666 — The promise of the sign-out that runs now, or null when no sign-out
 *  runs. One sign-out runs at a time, so this is also the sign-out of the
 *  page load (`finishPendingSignOut()`). A sign-in by email waits for it
 *  (`login()`). */
function runningSignOut(): Promise<SignOutResult> | null {
  return signOutInflight;
}

async function runSignOut(): Promise<SignOutResult> {
  beginSignOut();
  let complete = false;
  let stored = false;
  try {
    // The key goes in before the first request. A tab that closes during the
    // sign-out keeps the key, and the next page load sends the POST again.
    stored = markSignOutPending();
    if (refreshInflight) await waitAtMost(refreshInflight, SIGN_OUT_WAIT_MS);
    // The sign-out aborts a refresh that still runs after the wait, before the POST.
    refreshStop?.abort();
    await waitAtMost(releasePushSubscription(), PUSH_RELEASE_WAIT_MS);
    complete = await sendSignOut();
  } finally {
    if (complete) clearSignOutPending();
    // #213 — The local state goes in every case, also when the POST fails.
    useAuthStore.getState().clearAuth();
    endSignOut();
  }
  return { complete, retryAtNextLoad: stored && !complete };
}

/** #666 — The POST of a sign-out, 10 s at most. True on a 2xx answer. A
 *  failure goes to the telemetry, because the server can still hold the
 *  session. */
async function sendSignOut(): Promise<boolean> {
  // AbortController, not AbortSignal.timeout(): the build target includes
  // Safari 14 and 15, which do not have AbortSignal.timeout().
  const stop = new AbortController();
  const timer = setTimeout(() => stop.abort(), SIGN_OUT_WAIT_MS);
  try {
    const res = await fetch(`${API_BASE}/api/auth/email/logout`, { method: 'POST', credentials: 'include', signal: stop.signal });
    if (!res.ok) logBackgroundError('logout:post')(new Error(`The sign-out POST answered ${res.status}`));
    return res.ok;
  } catch (err) {
    // Offline, the backend is down, or no answer in 10 s.
    logBackgroundError('logout:post')(err);
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/** #666 — Wait for `work`, `ms` at most. The timer ends with the wait, also
 *  when `work` ends first. `work` must not reject. */
async function waitAtMost(work: Promise<unknown>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([work, new Promise<void>(resolve => { timer = setTimeout(resolve, ms); })]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * #666 — The boot of the app: a sign-out that did not reach the server goes
 * first. `App.tsx` calls this before the read of the user. The user chose to
 * sign out on this device, so the store starts with no user (`store/auth.ts`),
 * and the stored user goes here. Then the sign-out runs again, and a 2xx
 * answer removes the key.
 *
 * The callback page of a sign-in with a provider (#488) is the exception. The
 * API set the cookies of a NEW session before it sent the browser there, and
 * a POST would end that session. The sign-in on that page removes the key.
 *
 * A sign-in by email on the page waits until this sign-out ends (`login()`).
 * Thus a late answer of the POST cannot remove the cookies of the new session.
 *
 * The exception of a failed POST (`logout:post`) is best effort here: the
 * POST can fail before the telemetry SDK loads, and the exception then goes
 * nowhere. It carries no user id, because the local state goes first.
 */
export async function finishPendingSignOut(): Promise<void> {
  if (!signOutPending() || window.location.pathname.startsWith('/auth/callback')) return;
  useAuthStore.getState().clearAuth();
  await logout();
}

/**
 * #443 — a sign-out releases the Web Push subscription of this device. Before
 * 2026-09-23 the row stayed with the previous user, so a shared venue tablet
 * showed that user's pushes to the next person. `unsubscribe()` ends the
 * endpoint at the push service even when the DELETE fails; the backend then
 * removes the row at the next send (410 Gone, services/webPush.ts). `fetch`,
 * not `api`: on the axios path a 401 redirects to /login?reason=session_expired.
 */
async function releasePushSubscription(): Promise<void> {
  try {
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (!sub) return;
    const csrf = readCsrfCookie();
    await fetch(`${API_BASE}/api/notifications/push-subscribe`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
      credentials: 'include',
      body: JSON.stringify({ endpoint: sub.endpoint }),
    }).catch(() => { /* the unsubscribe below ends the endpoint anyway */ });
    await sub.unsubscribe();
  } catch { /* no service worker, or the subscription is already gone */ }
}

function redirectToLoginExpired(): void {
  // Best-effort server-side cookie clear. If this fails (network down),
  // the cookies will expire on their own; the user is redirected to
  // /login regardless. clearAuth() drops the local Zustand `user` blob, so
  // the pollers that read it stop at once.
  fetch(`${API_BASE}/api/auth/email/logout`, {
    method: 'POST',
    credentials: 'include',
  }).catch(() => { /* fire-and-forget */ });
  useAuthStore.getState().clearAuth();
  // Preserve any existing path so the user can be sent back after sign-in (future work).
  const url = new URL('/login', window.location.origin);
  url.searchParams.set('reason', 'session_expired');
  window.location.href = url.toString();
}

/** Extended config — _retry marks a request that has already had a refresh attempt, so we
 *  don't loop refreshing on persistent 401s. _retryCount tracks transient-failure retries
 *  (network error, timeout, 5xx gateway) on idempotent requests — separate budget from the
 *  401 refresh flag so they don't interfere. */
interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _retryCount?: number;
  /** #666 — the sign-out stamp when the request began (`signOutStamp()`). */
  _signOutStamp?: number;
}

/** Retry budget for transient-failure recovery (in-arena WiFi flake, brief 502/503/504).
 *  GET only — never retry POST/PATCH/DELETE because non-idempotent writes can duplicate
 *  on the server side. The few backend endpoints with explicit dedup (verdict ON CONFLICT,
 *  scoring ON CONFLICT DO UPDATE) don't need client-side retry — they handle racing
 *  retries server-side already. */
const MAX_RETRIES = 2;
const RETRY_BACKOFF_MS = [300, 900];

/** Decide whether an axios error is "transient enough to retry once more". Network
 *  Error = browser couldn't deliver the request (no response received), most common
 *  in-arena failure mode. ECONNABORTED = our 10s timeout fired. 502/503/504 = transient
 *  upstream/gateway errors that often clear on the next attempt. Everything else (4xx,
 *  500 application error, unknown) is not retried — it's a real failure or a real
 *  server bug, and retrying would just make telemetry harder to read. */
function isRetryableTransient(err: AxiosError): boolean {
  if (err.message === 'Network Error') return true;
  if (err.code === 'ECONNABORTED') return true;
  const status = err.response?.status;
  return status === 502 || status === 503 || status === 504;
}

function delay(ms: number): Promise<void> {
  return new Promise(r => setTimeout(r, ms));
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const original = (err.config || {}) as RetriableConfig;
    const status = err.response?.status;
    const isRefreshCall = typeof original.url === 'string' && original.url.includes('/api/auth/email/refresh-token');
    // #447 — a 403 CSRF_FAILED is a lost `lmf_csrf` cookie, and a refresh sets
    // a new one. Production and staging share that cookie (`.muaythai.lt`, so
    // app.* can read what api.* sets), and a sign-out on one environment
    // deleted it for the other: every change failed with 403 for up to 1
    // hour, because only a 401 started a refresh.
    const csrfLost = status === 403 && (err.response?.data as { code?: string } | undefined)?.code === 'CSRF_FAILED';

    if ((status === 401 || csrfLost) && !isRefreshCall) {
      // #666 — The sign-out removed the cookies: no refresh, no redirect.
      if (endedBySignOut(original)) return rejectForSignOut(err);
      // Two JS-side signals for "this user should be authenticated":
      //   - Zustand `user` blob (in-memory + localStorage-persisted)
      //   - lmf_csrf cookie (NOT HttpOnly — writeAuthCookies() sets it at a
      //     sign-in and at each refresh, and the logout endpoint clears it,
      //     so it follows the session on the normal path)
      // Gating on EITHER closes a silent 401 loop: when the two fall out
      // of sync (multi-tab race, partial logout, hydration miss, manually
      // cleared localStorage), `hasUser` alone says "anonymous" and the
      // interceptor swallows 401s forever while background pollers retry.
      // Trusting `lmf_csrf` as the fallback signal triggers a
      // refresh attempt — which either recovers cleanly or 401s and tips
      // us into the logout branch. Either way, no infinite loop.
      const looksAuthenticated = !!useAuthStore.getState().user || !!readCsrfCookie();
      if (looksAuthenticated && !original._retry) {
        original._retry = true;
        const refreshed = await tryRefreshToken();
        // #666 — A sign-out can begin while the refresh runs. Check again
        // before the retry and before the redirect.
        if (endedBySignOut(original)) return rejectForSignOut(err);
        if (refreshed) {
          // New cookies set by refresh-token — original request will pick
          // them up automatically on retry (withCredentials).
          return api(original);
        }
        redirectToLoginExpired();
      } else if (looksAuthenticated) {
        // Retry already happened and still 401'd. Force a clean logout so
        // background pollers stop hammering the API.
        redirectToLoginExpired();
      }
      // else: truly anonymous request 401'd — nothing to clean up, just reject.
    }

    // ── Transient-failure retry (GET only) ────────────────────────────────
    // Idempotent reads can be safely retried on Network Error / timeout /
    // 502-504. POST/PATCH/DELETE are NEVER retried here — even if the
    // backend has dedup, retrying writes is the caller's choice. Refresh
    // call has its own retry semantics; skip.
    const method = (original.method || '').toLowerCase();
    if (method === 'get' && !isRefreshCall && isRetryableTransient(err)) {
      const attempts = original._retryCount ?? 0;
      if (attempts < MAX_RETRIES) {
        original._retryCount = attempts + 1;
        await delay(RETRY_BACKOFF_MS[attempts]);
        // If api(original) succeeds, log a recovery event for diagnostic
        // visibility (no exception). If it fails again, the response
        // interceptor re-fires with _retryCount incremented and falls
        // through this block when the budget is exhausted, then continues
        // to the original error-handling path below.
        const result = await api(original);
        record({
          event: 'http_retry_succeeded',
          props: {
            url: original.url ?? '',
            attempts: String(original._retryCount),
            reason: err.message === 'Network Error' ? 'network'
                  : err.code === 'ECONNABORTED'      ? 'timeout'
                  : `http_${status ?? 'unknown'}`,
          },
        });
        return result;
      }
      // Budget exhausted — fall through. logBackgroundError() in
      // observability.ts classifies the rejection (background poll vs
      // foreground action) and pick trackEvent vs trackException.
    }

    if (typeof status === 'number' && status >= 500) {
      trackException(err, { url: err.config?.url ?? '', status: String(status) });
    }
    return Promise.reject(err);
  }
);

export default api;

// ── Email/password auth ─────────────────────────────────────────────────────
// Phase 3: response shape is { user, csrfToken } — no legacy token field.
// Bearer auth is gone; the cookie jar carries the access credential, and
// the csrfToken value is echoed into the X-CSRF-Token header on mutations.

/** #666 — A sign-in by email waits for a sign-out that runs: the sign-out of
 *  the page load (`finishPendingSignOut()`) or a tap on Sign out. Only the
 *  answer to the POST of a sign-out removes the session cookies. If that
 *  answer comes after the sign-in, it removes the cookies of the NEW session.
 *  The end of the sign-out also clears the user of the store (`clearAuth()`).
 *  The wait is in this function, so each caller waits. With no sign-out, the
 *  sign-in does not wait. On a stalled network a sign-out takes about 23 s at
 *  most, and the sign-in button shows its busy label during the wait. The
 *  sign-in with a provider does not wait (`loginWithSocial()`). */
export async function login(email: string, password: string) {
  const signOut = runningSignOut();
  // An unexpected error of the sign-out does not stop the sign-in.
  if (signOut) await signOut.catch(() => null);
  return api.post<{ user: User; csrfToken: string }>('/api/auth/email/login', { email, password });
}
export const register = (data: RegisterDto) =>
  api.post<{ message: string }>('/api/auth/email/register', data);
// #661 — the resend of the verification email: Login and Register use this one call.
export const resendVerification = (email: string) =>
  api.post<{ message: string }>('/api/auth/email/resend-verification', { email });
// Backend returns the user row directly (NOT wrapped in { user }) per
// backend/src/routes/auth.ts /me handler. Profile.tsx uses the same flat shape.
export const getMe = () => api.get<User>('/api/auth/email/me');

// ── Users (#664, F07) ───────────────────────────────────────────────────────
/** The list of the users, for a tournament manager and above. `role` names one role or a list. The
 *  route reads ONE `role` parameter with the names joined by a comma, and it refuses a repeated key
 *  (`role=a&role=b`), so this function joins the list. `q` searches the name and the email. */
export const getUsers = (query?: { role?: Role | Role[]; q?: string }) =>
  api.get<UserListRow[]>('/api/users', {
    params: { role: ([] as Role[]).concat(query?.role ?? []).join(',') || undefined, q: query?.q },
  });

// ── The sign-in with a provider (#488) ──────────────────────────────────────
// A browser navigation to the API, never a fetch: the API writes the flow
// cookie and redirects to the provider. The provider sends the browser back to
// the API, which sets the session cookies and redirects to /auth/callback (or
// to /login?error=<CODE>). `returnTo` names this origin; the API accepts it
// only when FRONTEND_URL lists it.
//
// #666 — The start of this sign-in does not wait for a sign-out that runs.
// The API sets the cookies of the new session only at the callback. Before
// the callback, Google shows its account chooser (`prompt=select_account`),
// and that page unloads this page. The unload aborts the POST of the
// sign-out, because the POST has no `keepalive`. An answer that comes before
// the unload removes only the old cookies. So no answer of the sign-out can
// come after the cookies of the new session. If the POST did not reach the
// server, the old session row stays live (`agent-docs/auth.md`, the accepted
// gaps). The sign-in on /auth/callback removes the key.
export const loginWithSocial = (provider: 'google'): void => {
  const returnTo = encodeURIComponent(window.location.origin);
  window.location.assign(`${API_BASE}/api/auth/sign-in/${provider}?returnTo=${returnTo}`);
};

// ── Admin: pending team members ─────────────────────────────────────────────
export const getAdminPendingMembers = () =>
  api.get<Array<Record<string, unknown>>>('/api/teams/admin/pending-members');
export const resolveAdminPendingMember = (memberId: string, status: string) =>
  api.patch(`/api/teams/admin/pending-members/${memberId}`, { status });

// ── Admin/TM: aggregate pending counts (drives nav badge) ───────────────────
export interface AdminPendingCounts {
  roles: number;
  claims: number;
  members: number;
  athletes: number;
  /** The pending athlete record claims (#376). They were in no counter. */
  athlete_claims: number;
  /** The team invitations of records with no account (#658, #664). The nav badge missed them. */
  athlete_invites: number;
  total: number;
}
export const getAdminPendingCounts = () =>
  api.get<AdminPendingCounts>('/api/users/admin/pending-counts');
/** The requests for a role (admin only). No `status` reads every request. */
export const getRoleRequests = (status?: RoleRequestRow['status']) =>
  api.get<RoleRequestRow[]>('/api/users/admin/role-requests', { params: { status } });

// ── Athletes ────────────────────────────────────────────────────────────────
export const getAthletes = (opts?: { includeInactive?: boolean }) =>
  api.get<AthleteEntry[]>('/api/athletes', {
    params: opts?.includeInactive ? { include_inactive: 'true' } : undefined,
  });
/** The athlete page (#595, #199): the athlete and the bouts of the athlete.
 *  The weight is NUMERIC, and node-pg sends it as a string ("70.0"). The type
 *  of the field is `number | string | null`, and this function converts the
 *  string to a number at the boundary, as getTeamAthletes does (#457). */
export const getAthlete = async (id: string) => {
  const res = await api.get<AthleteDetailResponse>(`/api/athletes/${id}`);
  const kg = res.data.athlete.current_weight_kg;
  res.data.athlete = { ...res.data.athlete, current_weight_kg: kg == null ? undefined : Number(kg) };
  return res;
};
/** #616 — the statistics of the officials. The list and the summary are for
 *  canSeeAllOfficialStats (admin, tournament_manager, jury); a referee reads
 *  the own page only. */
export const getOfficials = () => api.get<{ officials: OfficialRow[] }>('/api/officials');
export const getOfficial = (userId: string) => api.get<OfficialPage>(`/api/officials/${userId}`);
// #641, #642 — the profile of an official. Each write answers with the new profile.
export const addOfficialTeamPeriod = (userId: string, body: { team_id: string | null; valid_from: string }) =>
  api.post<{ profile: OfficialProfile }>(`/api/officials/${userId}/teams`, body);
export const removeOfficialTeamPeriod = (userId: string, periodId: string) =>
  api.delete<{ profile: OfficialProfile }>(`/api/officials/${userId}/teams/${periodId}`);
export const addOfficialAccreditation = (userId: string, body: { valid_from: string; valid_until: string | null }) =>
  api.post<{ profile: OfficialProfile }>(`/api/officials/${userId}/accreditations`, body);
export const setOfficialAccreditationEnd = (userId: string, accreditationId: string, validUntil: string | null) =>
  api.patch<{ profile: OfficialProfile }>(`/api/officials/${userId}/accreditations/${accreditationId}`, { valid_until: validUntil });
export const removeOfficialAccreditation = (userId: string, accreditationId: string) =>
  api.delete<{ profile: OfficialProfile }>(`/api/officials/${userId}/accreditations/${accreditationId}`);
export const getOfficialsSummary = (tournamentId: string) =>
  api.get<{ officials: OfficialsSummaryRow[] }>(`/api/officials/tournament/${tournamentId}/summary`);
// #647 — the report of the officials of one tournament (the Summary modal).
export const getOfficialsReport = (tournamentId: string) =>
  api.get<OfficialsReport>(`/api/officials/tournament/${tournamentId}/report`);
// Post users-athlete-split: every athlete is an athlete_records row, so
// reassign-team just updates ar.team_id. The legacy `source` parameter is gone.
export const reassignAthleteTeam = (id: string, team_id: string) =>
  api.patch(`/api/athletes/${id}/reassign-team`, { team_id });

// ── Athlete self-profile (the user's own AR via linked_user_id) ─────────────
// Frontend Profile.tsx + AthleteJoinTeamModal use these to manage gender/DOB/
// weight/experience. Returns null when the user has no athlete profile.
export const getMyAthlete = () =>
  api.get<AthleteRecord | null>('/api/athletes/me');
export const createMyAthlete = (data: CreateAthleteDto) =>
  api.post<{ athlete: AthleteRecord; request: AthleteTeamRequest }>('/api/athletes/me', data);
export const updateMyAthlete = (data: UpdateAthleteDto) =>
  api.patch<AthleteRecord>('/api/athletes/me', data);

// ── Athlete team requests (join-as-athlete approval workflow) ───────────────
export const requestTeamJoinAsAthlete = (team_id: string) =>
  api.post<AthleteTeamRequest>('/api/athletes/me/team-requests', { team_id });
export const getMyTeamRequests = () =>
  api.get<AthleteTeamRequest[]>('/api/athletes/me/team-requests');
// #658 — the team invitations: a coach sends one, the athlete (or, for a
// record with no account, an admin or a team manager) approves or rejects it.
export const createTeamInvite = (teamId: string, athleteRecordId: string) =>
  api.post<Omit<AthleteTeamInvite, 'team_name'>>(`/api/teams/${teamId}/athlete-invites`, { athlete_record_id: athleteRecordId });
export const getMyTeamInvites = () =>
  api.get<AthleteTeamInvite[]>('/api/athletes/me/team-invites');
export const resolveMyTeamInvite = (inviteId: string, status: 'approved' | 'rejected') =>
  api.patch(`/api/athletes/me/team-invites/${inviteId}`, { status });
export const getStaffTeamInvites = () =>
  api.get<AthleteTeamInvite[]>('/api/teams/admin/athlete-invites');
export const resolveStaffTeamInvite = (inviteId: string, status: 'approved' | 'rejected') =>
  api.patch(`/api/teams/admin/athlete-invites/${inviteId}`, { status });
export const getTeamAthleteRequests = (teamId: string) =>
  api.get<AthleteTeamRequest[]>(`/api/teams/${teamId}/athlete-requests`);
export const resolveAthleteRequest = (
  teamId: string, requestId: string, status: 'approved' | 'rejected'
) =>
  api.patch<AthleteTeamRequest>(`/api/teams/${teamId}/athlete-requests/${requestId}`, { status });

// ── Weigh-in / notifications / subscriptions ────────────────────────────────
export const getWeighins = (tournamentId: string, date: string) =>
  api.get<WeighinDayResponse>(`/api/tournaments/${tournamentId}/weighins`, { params: { date } });
export const getTeamRatings = (tournamentId: string) =>
  api.get<Array<Record<string, unknown>>>(`/api/tournaments/${tournamentId}/team-ratings`);
export const getNotifications = () =>
  api.get<{ notifications: Notification[]; unread_count: number }>('/api/notifications');
export const getUnreadCount = () =>
  api.get<{ unread_count: number }>('/api/notifications/unread-count');
export const getBoutSubscription = (boutId: string) =>
  api.get<{ subscribed: boolean }>(`/api/bouts/${boutId}/subscription`);
export const subscribeToBout = (boutId: string) =>
  api.post<{ subscribed: true }>(`/api/bouts/${boutId}/subscribe`);
export const unsubscribeFromBout = (boutId: string) =>
  api.delete<{ subscribed: false }>(`/api/bouts/${boutId}/subscribe`);
export const getVapidKey = () =>
  api.get<{ publicKey: string }>('/api/notifications/vapid-key');
export const savePushSubscription = (sub: PushSubscriptionDto) =>
  api.post('/api/notifications/push-subscribe', sub);
/** #664 (F07) — the Disable of the bell: the server forgets the endpoint of this device.
 *  The sign-out sends the same DELETE with `fetch` in `releasePushSubscription()`, as intended.
 *  A sign-out must not start the 401 refresh or the redirect of this client. It has its own 3 s limit. */
export const deletePushSubscription = (endpoint: string) =>
  api.delete('/api/notifications/push-subscribe', { data: { endpoint } });
export const markNotificationRead = (id: string) =>
  api.patch(`/api/notifications/${id}/read`);
export const markAllNotificationsRead = () =>
  api.patch('/api/notifications/read-all');
export const deleteNotification = (id: string) =>
  api.delete(`/api/notifications/${id}`);
/** The body of a push ping (backend `POST /api/notifications/push-ping`). A manager sends
 *  `tournament_id`, the jury of a bout sends `bout_id` and `tournament_id`, an admin sends neither. */
export interface PushPingBody {
  user_id: string;
  /** #557, #664 — the role of the sender, in the language of the sender: a text of `t.roles`. */
  title: string;
  body: string;
  /** A path inside the app. The notice opens it. */
  url?: string;
  tournament_id?: string;
  bout_id?: string;
}
/** #664 (F40) — the one door of the push ping. `PingModal` is the only caller. */
export const pushPing = (body: PushPingBody) =>
  api.post<{ message: string }>('/api/notifications/push-ping', body);
/** The weigh-in row of the day. #549 — `warnings` carries
 *  WEIGHT_OVER_CLASS_LIMIT at a championship (the manager is trusted). */
export const markWeighin = (tournamentId: string, registrationId: string, data: MarkWeighinDto) =>
  api.patch<{ status: WeighinStatus; weight_kg: number | string | null; warnings: ApiWarning[] }>(
    `/api/tournaments/${tournamentId}/weighins/${registrationId}`, data);
export const endWeighin = (tournamentId: string, date: string) =>
  api.post<{ message: string; wo_count: number; warnings?: ApiWarning[] }>(
    `/api/tournaments/${tournamentId}/end-weighin`, { date });
/** The answer is `direct` when the End needs no second person
 *  (`fourEyeRequired()` is false): an open ring or a hybrid (#547), or a
 *  championship that lists ONE manager (#557). A championship that lists two
 *  or more managers stores the proposal and answers it (#548). */
export const proposeEndWeighin = (tournamentId: string, date: string) =>
  api.post<{ direct: true; message: string } | ({ direct?: undefined; proposed: true } & WeighinProposal)>(
    `/api/tournaments/${tournamentId}/propose-end-weighin`, { date });
export const cancelEndWeighin = (tournamentId: string, date: string) =>
  api.post<{ message: string; cancelled: boolean }>(`/api/tournaments/${tournamentId}/cancel-end-weighin`, { date });

// ── Version ─────────────────────────────────────────────────────────────────
/** The version of the backend. `VersionCheck` polls it, and the Reload button of `Layout` uses it as a
 *  probe with a short `timeout`. `observability.ts` lists the path in `BACKGROUND_POLL_PATHS` as a
 *  literal: this file imports that one, so it cannot import the path. `api-boundary-census-664.spec.ts` pins the pair. */
export const getVersion = (opts?: { timeout?: number }) =>
  api.get<{ version: string }>('/api/version', opts);

// ── Tournaments ─────────────────────────────────────────────────────────────
export const getTournaments = () => api.get<Tournament[]>('/api/tournaments');
export const getTournament = (id: string) => api.get<Tournament>(`/api/tournaments/${id}`);
export const createTournament = (data: CreateTournamentDto) =>
  api.post<Tournament>('/api/tournaments', data);
export const updateTournament = (id: string, data: UpdateTournamentDto) =>
  api.patch<Tournament>(`/api/tournaments/${id}`, data);
export const deleteTournament = (id: string) => api.delete(`/api/tournaments/${id}`);
export const getTournamentRegistrations = (id: string) =>
  api.get<Registration[]>(`/api/tournaments/${id}/registrations`);
export const registerAthlete = (tournamentId: string, data: RegisterAthleteDto) =>
  api.post<Registration>(`/api/tournaments/${tournamentId}/registrations`, data);
/** #664 (F07) — change one registration. Send only the keys that change: the route writes the keys
 *  that the body names. The answer is the bare row of the table, not the list row `Registration`. */
export const updateRegistration = (tournamentId: string, registrationId: string, data: UpdateRegistrationDto) =>
  api.patch<RegistrationRow>(`/api/tournaments/${tournamentId}/registrations/${registrationId}`, data);
export const deleteRegistration = (tournamentId: string, registrationId: string) =>
  api.delete<SuccessAnswer>(`/api/tournaments/${tournamentId}/registrations/${registrationId}`);

// ── Referee pool ────────────────────────────────────────────────────────────
export const getTournamentReferees = (id: string) =>
  api.get<TournamentReferee[]>(`/api/tournaments/${id}/referees`);
/** `user_id` is the manager who adds another official (#455); no `user_id` joins the signed-in user.
 *  `available_dates: null` means every day. */
export const joinRefereePool = (
  tournamentId: string,
  data?: UpdateRefereeEntryDto & { user_id?: string }
) => api.post(`/api/tournaments/${tournamentId}/referees`, data || {});
export const updateMyPoolEntry = (tournamentId: string, data: UpdateRefereeEntryDto) =>
  api.patch(`/api/tournaments/${tournamentId}/referees/me`, data);
/** The answer names the pending bouts that lost an official of the pool, in `warnings` (#589). */
export const leaveRefereePool = (tournamentId: string) =>
  api.delete<SuccessAnswer & WithWarnings>(`/api/tournaments/${tournamentId}/referees/me`);
export const removeRefereeFromPool = (tournamentId: string, userId: string) =>
  api.delete<SuccessAnswer & WithWarnings>(`/api/tournaments/${tournamentId}/referees/${userId}`);
export const updateRefereeEntry = (tournamentId: string, userId: string, data: UpdateRefereeEntryDto) =>
  api.patch(`/api/tournaments/${tournamentId}/referees/${userId}`, data);

// ── Rings ───────────────────────────────────────────────────────────────────
export const createRing = (
  tournamentId: string,
  name: string,
  opts?: {
    min_age_group?: string | null;
    max_age_group?: string | null;
    max_weight_kg?: number | null;
  }
) =>
  api.post<Ring>(`/api/tournaments/${tournamentId}/rings`, {
    name,
    min_age_group: opts?.min_age_group || null,
    max_age_group: opts?.max_age_group || null,
    max_weight_kg: opts?.max_weight_kg ?? null,
  });
export const deleteRing = (tournamentId: string, ringId: string) =>
  api.delete(`/api/tournaments/${tournamentId}/rings/${ringId}`);

// ── Tournament managers ─────────────────────────────────────────────────────
export const getTournamentManagers = (tournamentId: string) =>
  api.get<TournamentManager[]>(`/api/tournaments/${tournamentId}/managers`);
export const addTournamentManager = (tournamentId: string, userId: string) =>
  api.post(`/api/tournaments/${tournamentId}/managers`, { user_id: userId });
export const removeTournamentManager = (tournamentId: string, userId: string) =>
  api.delete(`/api/tournaments/${tournamentId}/managers/${userId}`);

// ── Brackets ────────────────────────────────────────────────────────────────
export const getBrackets = (tournamentId: string) =>
  api.get<Bracket[]>(`/api/brackets/tournament/${tournamentId}`);
export const getBracketDetail = (bracketId: string) =>
  api.get<Bracket>(`/api/brackets/${bracketId}`);
/** Each call is a new IFMA lot (#465): the byes and the pairs by lot. */
export const previewBracket = (
  tournamentId: string,
  age_group: string,
  weight_class: string,
  gender?: string,
) =>
  api.post<Record<string, unknown>>(
    `/api/brackets/tournament/${tournamentId}/preview`,
    { age_group, weight_class, gender }
  );
export const confirmBracket = (tournamentId: string, data: ConfirmBracketDto) =>
  api.post<Bracket>(`/api/brackets/tournament/${tournamentId}/confirm`, data);
export const generateBoutPlan = (tournamentId: string) =>
  api.post<{ total: number; message: string; warnings: ApiWarning[] }>(
    `/api/brackets/tournament/${tournamentId}/generate-plan`
  );
export const getBoutPlan = (tournamentId: string) =>
  api.get<BoutPlanView>(`/api/brackets/tournament/${tournamentId}/plan`);
export const updatePlanBoutRing = (tournamentId: string, boutPlanId: string, ringId: string) =>
  api.patch(`/api/brackets/tournament/${tournamentId}/plan/bout-ring`, {
    bout_plan_id: boutPlanId, ring_id: ringId,
  });
export const reorderPlanBout = (tournamentId: string, boutPlanId: string, newPosition: number) =>
  api.patch(`/api/brackets/tournament/${tournamentId}/plan/reorder`, {
    bout_plan_id: boutPlanId, new_position: newPosition,
  });
export const confirmBoutPlan = (tournamentId: string) =>
  api.post<SuccessAnswer & { total_bouts: number }>(`/api/brackets/tournament/${tournamentId}/plan/confirm`);
export const discardBoutPlan = (tournamentId: string) =>
  api.delete(`/api/brackets/tournament/${tournamentId}/plan`);
export const getBracketBouts = (tournamentId: string) =>
  api.get<{
    bouts: Bout[];
    rings: Ring[];
    days: string[];
  }>(`/api/brackets/tournament/${tournamentId}/bracket-bouts`);
export const createManualBouts = (tournamentId: string, assignments: ManualBoutAssignment[]) =>
  api.post<SuccessAnswer & { total_bouts: number }>(
    `/api/brackets/tournament/${tournamentId}/create-manual-bouts`,
    { assignments }
  );
export const reassignBouts = (tournamentId: string, assignments: ReassignBoutAssignment[]) =>
  api.patch<SuccessAnswer & { updated: number; warnings: ApiWarning[] }>(
    `/api/brackets/tournament/${tournamentId}/reassign-bouts`,
    { assignments }
  );
export const startDay = (tournamentId: string, date: string) =>
  api.post<SuccessAnswer & { warnings?: ApiWarning[] }>(`/api/bouts/tournament/${tournamentId}/start-day`, { date });
export const deleteBracket = (bracketId: string) =>
  api.delete(`/api/brackets/${bracketId}`);
export const resetBouts = (tournamentId: string) =>
  api.post<SuccessAnswer & { bouts_deleted: number; brackets_reset: number }>(`/api/brackets/tournament/${tournamentId}/reset-bouts`);
export const openRingPreview = (tournamentId: string) =>
  api.post<{
    pairs: Array<Record<string, unknown>>;
    unmatched: Array<Record<string, unknown>>;
    total: number;
  }>(`/api/brackets/tournament/${tournamentId}/open-ring-preview`);
export const openRingConfirm = (tournamentId: string, pairs: OpenRingPair[]) =>
  api.post<SuccessAnswer & { created: number; skipped: number; warnings: ApiWarning[] }>(
    `/api/brackets/tournament/${tournamentId}/open-ring-confirm`,
    { pairs }
  );

// ── Bouts ───────────────────────────────────────────────────────────────────
export const getBouts = (tournamentId: string) =>
  api.get<Bout[]>(`/api/bouts/tournament/${tournamentId}`);
/** The last argument is the transport. The ring screens pass `publicApi` (no cookie). */
export const getBout = (id: string, via: AxiosInstance = api) => via.get<Bout>(`/api/bouts/${id}`);
/** #604, #608 — the next bout of a ring, by the rule of the notices.
 *  The route has no auth, so this call always uses `publicApi`.
 *  The answer also names the ring, for a ring with no bout yet. `id` is null when no bout waits. */
export const getRingNextBout = (tournamentId: string, ringId: string) =>
  publicApi.get<{ id: string | null; ring_name: string | null }>(
    `/api/bouts/tournament/${tournamentId}/ring/${ringId}/next`);
/** `warnings` holds the non-blocking advice of the write (the rest gap, a locked tournament, a ring cap). */
export const createBout = (data: CreateBoutDto) => api.post<Bout & WithWarnings>('/api/bouts', data);
export const updateBout = (id: string, data: UpdateBoutDto) =>
  api.patch<Bout & WithWarnings>(`/api/bouts/${id}`, data);
export const deleteBout = (id: string) => api.delete<SuccessAnswer>(`/api/bouts/${id}`);
export const assignSelfToBout = (boutId: string) =>
  api.post(`/api/bouts/${boutId}/referees/me`);
export const removeSelfFromBout = (boutId: string) =>
  api.delete(`/api/bouts/${boutId}/referees/me`);
/** The jury seat of a bout. No `juryUserId`: the signed-in jury claims the seat for itself.
 *  With `juryUserId`: a manager seats that jury member. */
export const assignBoutJury = (tournamentId: string, boutId: string, juryUserId?: string) =>
  api.post(`/api/tournaments/${tournamentId}/bouts/${boutId}/jury`, juryUserId ? { jury_user_id: juryUserId } : {});
export const removeBoutJury = (tournamentId: string, boutId: string) =>
  api.delete<SuccessAnswer>(`/api/tournaments/${tournamentId}/bouts/${boutId}/jury`);
/** Update which rounds are shown on the public scoreboard — persists immediately, broadcasts via socket.
 *  A round that fewer than 3 judges scored answers 409 ROUND_FEW_CARDS until
 *  the jury confirms it with `confirmFewCards` (#453). */
export const updateDisplayRounds = (boutId: string, display_rounds: number[], confirmFewCards = false) =>
  api.patch(`/api/bouts/${boutId}/display-rounds`,
    confirmFewCards ? { display_rounds, confirm_few_cards: true } : { display_rounds });
/** #601 — the jury sends the state of its backup timer to the ring display.
 *  Only the jury of the live bout: 403 TIMER_NOT_JURY, 409 TIMER_BOUT_NOT_LIVE. */
export const putBoutTimer = (boutId: string, state: BoutTimerInput) =>
  api.put<BoutTimerState>(`/api/bouts/${boutId}/timer`, state);
/** #601 — the newest state, as the ring display reads it (public). The last argument is the transport. */
export const getBoutTimer = (boutId: string, via: AxiosInstance = api) =>
  via.get<BoutTimerState>(`/api/bouts/${boutId}/timer`);
export const saveRoundOverride = (
  boutId: string, round: number, red: number | null, blue: number | null
) =>
  api.patch(`/api/scoring/bouts/${boutId}/overrides`, { round, red, blue });

// ── Scoring ─────────────────────────────────────────────────────────────────
export const submitRoundScore = (boutId: string, data: SubmitRoundScoreDto) =>
  api.post(`/api/scoring/bouts/${boutId}/rounds`, data);
/** The last argument is the transport. With `publicApi` the view is always 'public': the approved rounds only. */
export const getBoutScores = (boutId: string, via: AxiosInstance = api) =>
  via.get<BoutScoresResponse>(`/api/scoring/bouts/${boutId}`);

// ── Verdict ─────────────────────────────────────────────────────────────────
export const submitVerdict = (boutId: string, data: SubmitVerdictDto) =>
  api.post(`/api/verdict/bouts/${boutId}`, data);
export const assignRingReferee = (boutId: string, ring_referee_id: string) =>
  api.patch(`/api/verdict/bouts/${boutId}/ring-referee`, { ring_referee_id });

// ── Reference ───────────────────────────────────────────────────────────────
// The 11 IFMA v3.057 age groups in the one order, each row with `sort_order` (#344).
export const getAgeGroups = () => api.get<AgeGroup[]>('/api/reference/age-groups');
export const getWeightClasses = (params?: WeightClassQuery) =>
  api.get<WeightClass[]>('/api/reference/weight-classes', { params });
export const suggestWeightClass = (weight: number, age_group: string, gender: string) =>
  api.get<{ weight_class?: string }>('/api/reference/suggest-weight-class', {
    params: { weight, age_group, gender },
  });

// ── Upload ──────────────────────────────────────────────────────────────────
export const uploadAvatar    = (file: File)                             => uploadFile('/api/upload/avatar', file);
export const deleteAvatar    = ()                                        => api.delete('/api/upload/avatar');
export const uploadTeamLogo       = (teamId: string, file: File)         => uploadFile(`/api/upload/team-logo/${teamId}`, file);
export const deleteTeamLogo       = (teamId: string)                     => api.delete(`/api/upload/team-logo/${teamId}`);
export const uploadTournamentLogo = (tournamentId: string, file: File)   => uploadFile(`/api/upload/tournament-logo/${tournamentId}`, file);
export const deleteTournamentLogo = (tournamentId: string)               => api.delete(`/api/upload/tournament-logo/${tournamentId}`);

// ── Teams ───────────────────────────────────────────────────────────────────
export const getTeams = () => api.get<Team[]>('/api/teams');
export const getTeam = (id: string) => api.get<Team>(`/api/teams/${id}`);
export const createTeam = (data: CreateTeamDto) => api.post<Team>('/api/teams', data);
export const updateTeam = (id: string, data: UpdateTeamDto) =>
  api.patch<Team>(`/api/teams/${id}`, data);
export const joinTeam = (id: string, role: string) =>
  api.post(`/api/teams/${id}/join`, { role });
export const approveMember = (teamId: string, userId: string, status: string) =>
  api.patch(`/api/teams/${teamId}/members/${userId}`, { status });
/** `previous`: the user ids of the head coaches who became coaches (one head coach for each team). */
export const promoteTeamMember = (teamId: string, userId: string) =>
  api.patch<{ message: string; previous: string[] }>(`/api/teams/${teamId}/members/${userId}/promote`);
export const demoteTeamMember = (teamId: string, userId: string) =>
  api.patch(`/api/teams/${teamId}/members/${userId}/demote`);
export const removeTeamMember = (teamId: string, userId: string) =>
  api.delete(`/api/teams/${teamId}/members/${userId}`);

// ── Unified athlete list ────────────────────────────────────────────────────
export const getTeamAthletes = async (teamId: string, tournamentId?: string) => {
  const res = await api.get<AthleteEntry[]>(`/api/teams/${teamId}/athletes`, {
    params: tournamentId ? { tournament_id: tournamentId } : {},
  });
  // #457 — node-pg returns NUMERIC as a string ("57.0"). The type of the
  // field is `number | string | null`, and this function converts the string
  // to a number at the boundary: `kg === "57.0"` was never true, so every blur
  // on a weight field sent a save.
  res.data = res.data.map(a => ({
    ...a,
    current_weight_kg: a.current_weight_kg == null ? undefined : Number(a.current_weight_kg),
  }));
  return res;
};

// ── Athlete records ─────────────────────────────────────────────────────────
export const getAthleteRecords = (teamId: string) =>
  api.get<AthleteRecordRow[]>(`/api/athlete-records/teams/${teamId}`);
export const createAthleteRecord = (teamId: string, data: CreateAthleteRecordDto) =>
  api.post<AthleteRecord>(`/api/athlete-records/teams/${teamId}`, data);
export const updateAthleteRecord = (id: string, data: UpdateAthleteRecordDto) =>
  api.patch<AthleteRecord>(`/api/athlete-records/${id}`, data);
/** #674 — a coach saves the weight or the bout count of an athlete who has an account (Register
 *  Boxers). The path takes the USER id (`linked_user_id`), not the id of the athlete record (#457). */
export const coachEditAthlete = (userId: string, data: CoachEditAthleteDto) =>
  api.patch<CoachEditedAthlete>(`/api/users/${userId}/coach-edit`, data);
export const deleteAthleteRecord = (id: string) =>
  api.delete(`/api/athlete-records/${id}`);
// #569 — the birth date goes in the body, never in the URL.
export const claimAthleteRecord = (id: string, birthDate: string) =>
  api.post(`/api/athlete-records/${id}/claim`, { birth_date: birthDate });
export const unclaimAthleteRecord = (id: string) =>
  api.post(`/api/athlete-records/${id}/unclaim`);
export const reactivateAthleteRecord = (id: string) =>
  api.post<AthleteRecord>(`/api/athlete-records/${id}/reactivate`);
/** #658 — the record stays active and public, with no team. */
export const removeAthleteFromTeam = (id: string) =>
  api.post<{ id: string; team_id: null }>(`/api/athlete-records/${id}/remove-from-team`);
/** #578 — merge the record `id` into `keepId` (admin only). With
 *  `dryRun`, the server runs the checks and the moves, then rolls back: the
 *  answer says what would move, and nothing changes. */
export const mergeAthleteRecords = (id: string, keepId: string, dryRun = false) =>
  api.post<AthleteMergeResult>(`/api/athlete-records/${id}/merge`, { keep_id: keepId, dry_run: dryRun });
export const resolveClaimRequest = (claimId: string, status: string) =>
  api.patch(`/api/athlete-records/claims/${claimId}`, { status });
export const getTeamClaims = (teamId: string) =>
  api.get<AthleteRecordClaim[]>(`/api/athlete-records/teams/${teamId}/claims`);

// ── Assign coach to team (team_manager / admin) ─────────────────────────────
export const assignCoachToTeam = (teamId: string, userId: string, role: string) =>
  api.post(`/api/teams/${teamId}/assign-coach`, { user_id: userId, role });

// ── Team claims ─────────────────────────────────────────────────────────────
export const claimTeam = (teamId: string, role: string) =>
  api.post(`/api/teams/${teamId}/claim`, { role });
export const getPendingTeamClaims = () =>
  api.get<TeamClaim[]>('/api/teams/claims/pending');
// #376 — every pending athlete record claim, for the admin queue. The
// per-team list needs a coach of that team, so the staff could see nothing.
export const getPendingAthleteClaims = () =>
  api.get<AdminAthleteClaim[]>('/api/athlete-records/claims/pending');
export const resolveTeamClaim = (claimId: string, status: string) =>
  api.patch(`/api/teams/claims/${claimId}`, { status });
